/**
 * Centralized configuration service for all discipline-related options
 * Single source of truth for configuration data across all discipline components
 */

import consequenceTypesService from './consequenceTypesService.js';
import { mentionTypesService } from './mentionTypesService.js';
import timeframesService from './timeframesService.js';
import studentHistoryFiltersService from './studentHistoryFiltersService.js';
import gradesService from './gradesService.js';
import schoolDatePeriodsService from './schoolDatePeriodsService.js';

// Legacy consequence types (kept for migration and reference)
// These will be seeded into Firestore on first run
export const LEGACY_CONSEQUENCE_TYPES = {
  DEMERIT: {
    value: 'demerit',
    label: 'Demerits',
    singular: 'demerit',
    plural: 'demerits',
    icon: '⚠️',
    color: 'warning',
    description: 'Minor consequence for infractions',
    severity: 'low',
    requiresAction: false,
    sortOrder: 0
  },
  WARNING: {
    value: 'warning',
    label: 'Warnings',
    singular: 'warning',
    plural: 'warnings',
    icon: '⚠️',
    color: 'warning',
    description: 'Verbal or written warning',
    severity: 'low',
    requiresAction: false,
    sortOrder: 1
  },
  DETENTION: {
    value: 'detention',
    label: 'Detentions',
    singular: 'detention',
    plural: 'detentions',
    icon: '⏱️',
    color: 'error',
    description: 'Time-based consequence requiring scheduling',
    severity: 'medium',
    requiresAction: true,
    subtypes: ['lunch', 'after-school', 'homework', 'friday', 'saturday'],
    sortOrder: 2
  },
  HOMEWORK_DETENTION: {
    value: 'homework-detention',
    label: 'Homework Detentions',
    singular: 'homework detention',
    plural: 'homework detentions',
    icon: '📚',
    color: 'warning',
    description: 'Detention focused on completing homework',
    severity: 'medium',
    requiresAction: true,
    sortOrder: 3
  },
  FRIDAY_DETENTION: {
    value: 'friday-detention',
    label: 'Friday Detentions',
    singular: 'Friday detention',
    plural: 'Friday detentions',
    icon: '📅',
    color: 'error',
    description: 'Extended detention on Friday',
    severity: 'medium',
    requiresAction: true,
    sortOrder: 4
  },
  SATURDAY_DETENTION: {
    value: 'saturday-detention',
    label: 'Saturday Detentions',
    singular: 'Saturday detention',
    plural: 'Saturday detentions',
    icon: '🗓️',
    color: 'error',
    description: 'Weekend detention for serious infractions',
    severity: 'high',
    requiresAction: true,
    sortOrder: 5
  },
  SUSPENSION: {
    value: 'suspension',
    label: 'Suspensions',
    singular: 'suspension',
    plural: 'suspensions',
    icon: '🚫',
    color: 'error',
    description: 'Serious consequence requiring administrative action',
    severity: 'high',
    requiresAction: true,
    subtypes: ['in-school', 'out-of-school'],
    sortOrder: 6
  },
  PRIVILEGE_LOSS: {
    value: 'privilege-loss',
    label: 'Privilege Losses',
    singular: 'privilege loss',
    plural: 'privilege losses',
    icon: '🚷',
    color: 'warning',
    description: 'Removal of specific privileges',
    severity: 'medium',
    requiresAction: false,
    sortOrder: 7
  },
  REFERRAL: {
    value: 'referral',
    label: 'Office Referrals',
    singular: 'office referral',
    plural: 'office referrals',
    icon: '📋',
    color: 'error',
    description: 'Referral to administration',
    severity: 'high',
    requiresAction: true,
    sortOrder: 8
  },
  CONTRACT: {
    value: 'contract',
    label: 'Behavior Contracts',
    singular: 'behavior contract',
    plural: 'behavior contracts',
    icon: '📄',
    color: 'warning',
    description: 'Formal behavior agreement',
    severity: 'medium',
    requiresAction: true,
    sortOrder: 9
  },
  RESTORATIVE: {
    value: 'restorative',
    label: 'Restorative Actions',
    singular: 'restorative action',
    plural: 'restorative actions',
    icon: '🤝',
    color: 'success',
    description: 'Restorative justice approach',
    severity: 'low',
    requiresAction: true,
    subtypes: ['apology', 'community-service', 'peer-mediation'],
    sortOrder: 10
  }
};

// Legacy timeframe options (kept for migration and reference)
// These will be seeded into Firestore on first run
export const LEGACY_TIMEFRAME_OPTIONS = {
  DAY: {
    id: 'day',
    value: 'day',
    label: 'days',
    singular: 'day',
    plural: 'days',
    days: 1,
    description: 'Within the same day'
  },
  WEEK: {
    id: 'week',
    value: 'week',
    label: 'weeks',
    singular: 'week',
    plural: 'weeks',
    days: 7,
    description: 'Within the last 7 days'
  },
  MONTH: {
    id: 'month',
    value: 'month',
    label: 'months',
    singular: 'month',
    plural: 'months',
    days: 30,
    description: 'Within the last 30 days'
  },
  TERM: {
    id: 'term',
    value: 'term',
    label: 'terms',
    singular: 'term',
    plural: 'terms',
    days: 90,
    description: 'Current term (quarter)'
  },
  SEMESTER: {
    id: 'semester',
    value: 'semester',
    label: 'semesters',
    singular: 'semester',
    plural: 'semesters',
    days: 180,
    description: 'Current semester'
  },
  YEAR: {
    id: 'year',
    value: 'year',
    label: 'years',
    singular: 'year',
    plural: 'years',
    days: 365,
    description: 'Current school year'
  }
};

// Academic calendar timeframe options (new academic calendar-based timeframes)
export const ACADEMIC_TIMEFRAME_OPTIONS = {
  ACADEMIC_TERM: {
    id: 'academic-term',
    value: 'academic-term',
    label: 'academic terms',
    singular: 'academic term',
    plural: 'academic terms',
    description: 'Current academic term (calendar-aligned)',
    requiresAcademicCalendar: true,
    calendarBased: true
  },
  ACADEMIC_SEMESTER: {
    id: 'academic-semester',
    value: 'academic-semester',
    label: 'academic semesters',
    singular: 'academic semester',
    plural: 'academic semesters',
    description: 'Current academic semester (calendar-aligned)',
    requiresAcademicCalendar: true,
    calendarBased: true
  },
  ACADEMIC_YEAR: {
    id: 'academic-year',
    value: 'academic-year',
    label: 'academic years',
    singular: 'academic year',
    plural: 'academic years',
    description: 'Current academic year (calendar-aligned)',
    requiresAcademicCalendar: true,
    calendarBased: true
  }
};

// Severity levels
export const SEVERITY_LEVELS = {
  LOW: {
    id: 'low',
    value: 'low',
    label: 'Low',
    color: 'success',
    icon: '🟢',
    points: 1,
    description: 'Minor infractions'
  },
  MEDIUM: {
    id: 'medium',
    value: 'medium',
    label: 'Medium',
    color: 'warning',
    icon: '🟡',
    points: 3,
    description: 'Moderate infractions'
  },
  HIGH: {
    id: 'high',
    value: 'high',
    label: 'High',
    color: 'error',
    icon: '🔴',
    points: 5,
    description: 'Serious infractions'
  },
  CRITICAL: {
    id: 'critical',
    value: 'critical',
    label: 'Critical',
    color: 'error',
    icon: '⚫',
    points: 10,
    description: 'Zero-tolerance violations'
  }
};

// Legacy grade options (kept for migration and reference)
// These will be seeded into Firestore on first run
export const LEGACY_GRADE_OPTIONS = {
  K: { id: 'K', value: 'K', label: 'Kindergarten', order: 0 },
  '1': { id: '1', value: '1', label: 'Grade 1', order: 1 },
  '2': { id: '2', value: '2', label: 'Grade 2', order: 2 },
  '3': { id: '3', value: '3', label: 'Grade 3', order: 3 },
  '4': { id: '4', value: '4', label: 'Grade 4', order: 4 },
  '5': { id: '5', value: '5', label: 'Grade 5', order: 5 },
  '6': { id: '6', value: '6', label: 'Grade 6', order: 6 },
  '7': { id: '7', value: '7', label: 'Grade 7', order: 7 },
  '8': { id: '8', value: '8', label: 'Grade 8', order: 8 },
  '9': { id: '9', value: '9', label: 'Grade 9', order: 9 },
  '10': { id: '10', value: '10', label: 'Grade 10', order: 10 },
  '11': { id: '11', value: '11', label: 'Grade 11', order: 11 },
  '12': { id: '12', value: '12', label: 'Grade 12', order: 12 }
};

// Rule types
export const RULE_TYPES = {
  MENTION: {
    id: 'mention',
    value: 'mention',
    label: 'Mention Rule',
    icon: '💬',
    description: 'Triggered by specific offenses or mentions'
  },
  THRESHOLD: {
    id: 'threshold',
    value: 'threshold',
    label: 'Threshold Rule',
    icon: '📊',
    description: 'Triggered when reaching a certain number of incidents'
  },
  ZERO_TOLERANCE: {
    id: 'zero-tolerance',
    value: 'zero-tolerance',
    label: 'Zero Tolerance Rule',
    icon: '🚫',
    description: 'Immediate consequence for specific violations'
  },
  ACCOUNTABILITY: {
    id: 'accountability',
    value: 'accountability',
    label: 'Accountability Rule',
    icon: '✅',
    description: 'Tracking and accountability requirements'
  },
  CONTRACT: {
    id: 'contract',
    value: 'contract',
    label: 'Behavior Contract',
    icon: '📄',
    description: 'Formal behavior agreement with terms'
  }
};

// Mention scope options (how mentions are counted/scoped)
export const MENTION_SCOPE_OPTIONS = {
  SAME_OFFENSE: {
    id: 'same-offense',
    value: 'same-offense',
    label: 'for the same offense',
    singular: 'for the same offense',
    description: 'Count mentions only for the specific offense type'
  },
  SAME_CATEGORY: {
    id: 'same-category',
    value: 'same-category',
    label: 'in the same category',
    singular: 'in the same category',
    description: 'Count mentions within the same offense category'
  },
  SAME_LESSON: {
    id: 'same-lesson',
    value: 'same-lesson',
    label: 'in the same lesson',
    singular: 'in the same lesson',
    description: 'Count mentions only within the same lesson/period'
  },
  ANY: {
    id: 'any',
    value: 'any',
    label: 'of any type',
    singular: 'of any type',
    description: 'Count all mentions regardless of type'
  }
};

// Reset condition options (when mention counters reset)
export const RESET_CONDITION_OPTIONS = {
  NEVER: {
    id: 'never',
    value: 'never',
    label: 'Never reset',
    description: 'Counter never resets - cumulative throughout the year'
  },
  AFTER_CONSEQUENCE: {
    id: 'after-consequence',
    value: 'after-consequence',
    label: 'Reset after consequence',
    description: 'Counter resets after consequence is issued'
  },
  AFTER_PERIOD: {
    id: 'after-period',
    value: 'after-period',
    label: 'Reset after time period',
    description: 'Counter resets after specified time period'
  }
};

// Legacy student history filter options (kept for migration and reference)
// These will be seeded into Firestore on first run
export const LEGACY_STUDENT_HISTORY_OPTIONS = {
  ALL_STUDENTS: {
    id: 'all-students',
    value: null,
    label: 'All students',
    description: 'Apply to any student regardless of history'
  },
  FIRST_OFFENSE: {
    id: 'first-offense',
    value: 'first-offense',
    label: 'First offense only',
    description: 'Only students with no prior offenses'
  },
  TWO_PLUS: {
    id: '2-plus',
    value: '2-plus',
    label: '2+ offenses',
    description: 'Students with 2 or more prior offenses'
  },
  FIVE_PLUS: {
    id: '5-plus',
    value: '5-plus',
    label: '5+ offenses',
    description: 'Students with 5 or more prior offenses'
  }
};

// Queue status options
export const QUEUE_STATUS = {
  PENDING: {
    id: 'pending',
    value: 'pending',
    label: 'Pending',
    color: 'warning',
    icon: '⏳'
  },
  SCHEDULED: {
    id: 'scheduled',
    value: 'scheduled',
    label: 'Scheduled',
    color: 'info',
    icon: '📅'
  },
  IN_PROGRESS: {
    id: 'in-progress',
    value: 'in-progress',
    label: 'In Progress',
    color: 'primary',
    icon: '▶️'
  },
  SERVED: {
    id: 'served',
    value: 'served',
    label: 'Served',
    color: 'success',
    icon: '✅'
  },
  ARCHIVED: {
    id: 'archived',
    value: 'archived',
    label: 'Archived',
    color: 'neutral',
    icon: '📁'
  },
  CANCELLED: {
    id: 'cancelled',
    value: 'cancelled',
    label: 'Cancelled',
    color: 'error',
    icon: '❌'
  }
};

/**
 * Configuration Service API
 */
class DisciplineConfigService {
  constructor() {
    this._consequenceTypesCache = null;
  }

  /**
   * Get consequence types as array (async - queries Firestore)
   * @returns {Promise<Array>} Array of consequence type objects
   */
  async getConsequenceTypes(schoolId) {
    try {
      return await consequenceTypesService.getAll(schoolId);
    } catch (error) {
      console.error('Error loading consequence types from Firestore:', error);
      // Fallback to legacy types if Firestore fails
      console.warn('Falling back to legacy consequence types');
      return Object.values(LEGACY_CONSEQUENCE_TYPES);
    }
  }

  /**
   * Get consequence types for select components
   * @returns {Promise<Array>} Array of select options
   */
  async getConsequenceTypeOptions(schoolId) {
    const types = await this.getConsequenceTypes(schoolId);
    return types.map(type => ({
      value: type.value,
      label: type.label,
      singular: type.singular,
      plural: type.plural,
      icon: type.icon,
      description: type.description
    }));
  }

  /**
   * Get consequence type by value
   * @param {string} value - The consequence type value
   * @param {string} schoolId - The school ID
   * @returns {Promise<Object|null>} Consequence type object or null
   */
  async getConsequenceType(value, schoolId) {
    try {
      const types = await this.getConsequenceTypes(schoolId);
      // Try exact match first
      let type = types.find(t => t.value === value);
      if (type) return type;

      // Try case-insensitive match with underscores converted to hyphens
      const normalizedValue = value.toLowerCase().replace('_', '-');
      type = types.find(t => t.value === normalizedValue);
      if (type) return type;

      return null;
    } catch (error) {
      console.error('Error getting consequence type:', error);
      return null;
    }
  }

  /**
   * Get mention types as array (async - queries Firestore)
   * @param {string} schoolId - School ID for fetching mention types
   * @returns {Promise<Array>} Array of mention type objects
   */
  async getMentionTypes(schoolId) {
    try {
      return await mentionTypesService.getAll(schoolId);
    } catch (error) {
      console.error('Error loading mention types from Firestore:', error);
      return [];
    }
  }

  /**
   * Get mention types for select components
   * @returns {Promise<Array>} Array of select options
   */
  async getMentionTypeOptions(schoolId) {
    const types = await this.getMentionTypes(schoolId);
    return types.map(type => ({
      value: type.value,
      label: type.label
    }));
  }

  /**
   * Get timeframe options: rolling timeframes + school's custom date range types.
   * @param {string|null} schoolId - School ID to load custom types from dateRangeConfig
   * @returns {Promise<Array>} Array of timeframe objects
   */
  async getTimeframeOptions(schoolId = null) {
    // Fixed rolling timeframes (always available, no school config needed)
    const ROLLING = [
      { id: 'day', value: 'day', singular: 'day', plural: 'days', label: 'days', days: 1, description: 'Within the same day' },
      { id: 'week', value: 'week', singular: 'week', plural: 'weeks', label: 'weeks', days: 7, description: 'Within the last 7 days' },
      { id: 'month', value: 'month', singular: 'month', plural: 'months', label: 'months', days: 30, description: 'Within the last 30 days' },
    ];

    if (!schoolId) return ROLLING;

    try {
      const config = await schoolDatePeriodsService.getConfig(schoolId);
      const schoolTypes = (config?.types || [])
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(type => ({
          id: type.id,
          value: type.id,
          singular: type.name.toLowerCase(),
          plural: type.name.toLowerCase() + 's',
          label: type.name.toLowerCase() + 's',
          days: 0,
          calendarBased: true,
          description: `Current ${type.name.toLowerCase()} period`,
        }));

      return [...ROLLING, ...schoolTypes];
    } catch (error) {
      console.error('Error loading school date range config:', error);
      return ROLLING;
    }
  }

  /**
   * Get timeframe options for select components
   * @param {string|null} schoolId - School ID to load custom types
   * @returns {Promise<Array>} Array of select options
   */
  async getTimeframeSelectOptions(schoolId = null) {
    const options = await this.getTimeframeOptions(schoolId);
    return options.map(opt => ({
      value: opt.value,
      label: opt.label,
      singular: opt.singular,
      plural: opt.plural,
      description: opt.description,
      calendarBased: opt.calendarBased
    }));
  }

  /**
   * Get academic timeframe options only
   * @returns {Promise<Array>} Array of academic timeframe objects
   */
  async getAcademicTimeframeOptions() {
    try {
      const timeframes = await timeframesService.getAll();
      return timeframes.filter(tf => tf.requiresAcademicCalendar);
    } catch (error) {
      console.error('Error loading academic timeframes:', error);
      return Object.values(ACADEMIC_TIMEFRAME_OPTIONS);
    }
  }

  /**
   * Get standard timeframe options (non-academic)
   * @returns {Promise<Array>} Array of standard timeframe objects
   */
  async getStandardTimeframeOptions() {
    try {
      const timeframes = await timeframesService.getAll();
      return timeframes.filter(tf => !tf.requiresAcademicCalendar);
    } catch (error) {
      console.error('Error loading standard timeframes:', error);
      return Object.values(LEGACY_TIMEFRAME_OPTIONS);
    }
  }


  /**
   * Get timeframe by ID
   */
  getTimeframe(id) {
    return TIMEFRAME_OPTIONS[id.toUpperCase().replace('-', '_')] || null;
  }

  /**
   * Get severity levels as array
   */
  getSeverityLevels() {
    return Object.values(SEVERITY_LEVELS);
  }

  /**
   * Get severity options for select components
   */
  getSeverityOptions() {
    return this.getSeverityLevels().map(level => ({
      value: level.value,
      label: level.label,
      icon: level.icon,
      color: level.color
    }));
  }

  /**
   * Get severity level by ID
   */
  getSeverityLevel(id) {
    return SEVERITY_LEVELS[id.toUpperCase()] || null;
  }

  /**
   * Get grade options as array (async - queries school-scoped Firestore)
   * @param {string} schoolId - School ID
   */
  async getGradeOptions(schoolId) {
    if (!schoolId) {
      console.warn('[disciplineConfigService] getGradeOptions called without schoolId');
      return Object.values(LEGACY_GRADE_OPTIONS).sort((a, b) => a.order - b.order);
    }
    try {
      return await gradesService.getAll(schoolId);
    } catch (error) {
      console.warn('Error loading grades from Firestore, falling back to legacy grades:', error);
      return Object.values(LEGACY_GRADE_OPTIONS).sort((a, b) => a.order - b.order);
    }
  }

  /**
   * Get grade options for select components (async - queries school-scoped Firestore)
   * @param {string} schoolId - School ID
   */
  async getGradeSelectOptions(schoolId) {
    const grades = await this.getGradeOptions(schoolId);
    return grades.map(grade => ({
      value: grade.id,
      label: grade.name || grade.label
    }));
  }

  /**
   * Get rule types as array
   */
  getRuleTypes() {
    return Object.values(RULE_TYPES);
  }

  /**
   * Get queue status options
   */
  getQueueStatusOptions() {
    return Object.values(QUEUE_STATUS);
  }

  /**
   * Get student history filter options (for rules) - async, queries Firestore
   * @returns {Promise<Array>} Array of student history filter objects
   */
  async getStudentHistoryOptions() {
    try {
      return await studentHistoryFiltersService.getAll();
    } catch (error) {
      console.error('Error loading student history filters from Firestore:', error);
      // Fallback to legacy filters if Firestore fails
      console.warn('Falling back to legacy student history options');
      return Object.values(LEGACY_STUDENT_HISTORY_OPTIONS);
    }
  }

  /**
   * Get mention scope options
   */
  getMentionScopeOptions() {
    return Object.values(MENTION_SCOPE_OPTIONS);
  }

  /**
   * Get reset condition options
   */
  getResetConditionOptions() {
    return Object.values(RESET_CONDITION_OPTIONS);
  }

  /**
   * Transform any config array to select options format
   */
  toSelectOptions(items, valueKey = 'value', labelKey = 'label') {
    return items.map(item => ({
      value: item[valueKey],
      label: item[labelKey]
    }));
  }

  /**
   * Get filtered consequence types by severity
   * @returns {Promise<Array>} Filtered consequence types
   */
  async getConsequenceTypesBySeverity(severity, schoolId) {
    const types = await this.getConsequenceTypes(schoolId);
    return types.filter(type => type.severity === severity);
  }

  /**
   * Get consequence types that require action
   * @returns {Promise<Array>} Actionable consequence types
   */
  async getActionableConsequenceTypes(schoolId) {
    const types = await this.getConsequenceTypes(schoolId);
    return types.filter(type => type.requiresAction);
  }

  /**
   * Validate consequence type
   * @param {string} type - The consequence type to validate
   * @param {string} schoolId - The school ID
   * @returns {Promise<boolean>} True if valid
   */
  async isValidConsequenceType(type, schoolId) {
    const result = await this.getConsequenceType(type, schoolId);
    return !!result;
  }

  /**
   * Validate severity level
   */
  isValidSeverityLevel(level) {
    return !!this.getSeverityLevel(level);
  }

  /**
   * Get default consequence for severity
   */
  getDefaultConsequenceForSeverity(severity) {
    const severityMap = {
      low: 'demerit',
      medium: 'detention',
      high: 'suspension',
      critical: 'suspension'
    };
    return severityMap[severity] || 'demerit';
  }

  /**
   * Get color for consequence type
   * @param {string} type - The consequence type
   * @param {string} schoolId - The school ID
   * @returns {Promise<string>} Color value
   */
  async getConsequenceColor(type, schoolId) {
    const consequence = await this.getConsequenceType(type, schoolId);
    return consequence ? consequence.color : 'neutral';
  }

  /**
   * Get icon for consequence type
   * @param {string} type - The consequence type
   * @param {string} schoolId - The school ID
   * @returns {Promise<string>} Icon emoji
   */
  async getConsequenceIcon(type, schoolId) {
    const consequence = await this.getConsequenceType(type, schoolId);
    return consequence ? consequence.icon : '⚠️';
  }

  /**
   * Get event type configuration for DisciplineQueue display
   * Maps consequence types AND mention types to visual styling (colors, gradients, icons)
   * Now dynamically builds from Firestore data
   * @returns {Promise<Object>} Event type configuration object
   */
  async getEventTypeConfig(schoolId) {
    // Color mapping based on consequence severity/type
    const styleMap = {
      'demerit': {
        color: 'text-amber-600',
        bgColor: 'bg-gradient-to-br from-amber-50 to-yellow-50',
        borderColor: 'border-amber-200',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600'
      },
      'detention': {
        color: 'text-orange-600',
        bgColor: 'bg-gradient-to-br from-orange-50 to-red-50',
        borderColor: 'border-orange-200',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-600'
      },
      'friday-detention': {
        color: 'text-indigo-600',
        bgColor: 'bg-gradient-to-br from-indigo-50 to-blue-50',
        borderColor: 'border-indigo-200',
        iconBg: 'bg-indigo-100',
        iconColor: 'text-indigo-600'
      },
      'saturday-detention': {
        color: 'text-purple-600',
        bgColor: 'bg-gradient-to-br from-purple-50 to-violet-50',
        borderColor: 'border-purple-200',
        iconBg: 'bg-purple-100',
        iconColor: 'text-purple-600'
      },
      'homework-detention': {
        color: 'text-pink-600',
        bgColor: 'bg-gradient-to-br from-pink-50 to-rose-50',
        borderColor: 'border-pink-200',
        iconBg: 'bg-pink-100',
        iconColor: 'text-pink-600'
      },
      'suspension': {
        color: 'text-red-600',
        bgColor: 'bg-gradient-to-br from-red-50 to-rose-50',
        borderColor: 'border-red-200',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600'
      },
      'privilege-loss': {
        color: 'text-slate-600',
        bgColor: 'bg-gradient-to-br from-slate-50 to-gray-50',
        borderColor: 'border-slate-200',
        iconBg: 'bg-slate-100',
        iconColor: 'text-slate-600'
      },
      'referral': {
        color: 'text-emerald-600',
        bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50',
        borderColor: 'border-emerald-200',
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600'
      },
      'warning': {
        color: 'text-yellow-600',
        bgColor: 'bg-gradient-to-br from-yellow-50 to-amber-50',
        borderColor: 'border-yellow-200',
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-600'
      }
    };

    // Build config for ALL consequence types from Firestore (including inactive ones)
    // This ensures legacy data with inactive types still displays correctly
    const config = {};
    const types = await consequenceTypesService.getAllIncludingInactive(schoolId);
    types.forEach(type => {
      const styles = styleMap[type.value] || styleMap['demerit']; // fallback to demerit styles
      config[type.value] = {
        label: (type.label || type.value || 'Unknown').replace(/s$/, '').replace(/Demerits/, 'Demerit'), // Singular form for display
        ...styles,
        icon: type.icon, // Keep emoji icon from config
        inactive: type.active === false, // Mark inactive types
        requiresAction: type.requiresAction === true
      };
    });

    // Load mention types from Firestore with fallbacks to prevent "Unknown Type"
    try {
      const mentionTypes = await this.getMentionTypes(schoolId);
      
      // If no mention types exist, provide fallback configurations
      if (mentionTypes.length === 0) {
        console.warn('[disciplineConfigService] No mention types found, using fallback configurations');
        const fallbackMentionTypes = [
          { value: 'behavior', label: 'Behavior Mention', icon: '⚠️' },
          { value: 'homework', label: 'Homework Mention', icon: '📚' },
          { value: 'uniform', label: 'Uniform Mention', icon: '👔' },
          { value: 'punctuality', label: 'Punctuality Mention', icon: '⏰' }
        ];
        
        fallbackMentionTypes.forEach(type => {
          const styles = styleMap[type.value] || styleMap['demerit'];
          config[type.value] = {
            label: type.label,
            ...styles,
            icon: type.icon,
            isFallback: true
          };
        });
      } else {
        mentionTypes.forEach(type => {
          // Use the color from Firestore if available, otherwise fall back to styleMap
          const hexColor = type.color;
          const fallbackStyles = styleMap[type.value] || styleMap['demerit'];

          config[type.value] = {
            label: type.label.replace(/s$/, '').replace(/Mentions/, 'Mention'), // Singular form for display
            // If mentionType has a color defined, use it; otherwise use fallback styles
            ...(hexColor ? {
              hexColor: hexColor,
              borderHexColor: hexColor,
              color: fallbackStyles.color,
              bgColor: fallbackStyles.bgColor,
              borderColor: fallbackStyles.borderColor,
              iconBg: fallbackStyles.iconBg,
              iconColor: fallbackStyles.iconColor
            } : fallbackStyles),
            icon: type.icon || '⚠️',
            inactive: type.active === false
          };
        });
      }
    } catch (error) {
      console.error('Error loading mention types for eventTypeConfig:', error);
      // Critical: Add fallback mention types to prevent "Unknown Type"
      config.behavior = { label: 'Behavior Mention', ...styleMap['demerit'], icon: '⚠️', isFallback: true };
      config.homework = { label: 'Homework Mention', ...styleMap['demerit'], icon: '📚', isFallback: true };
      config.uniform = { label: 'Uniform Mention', ...styleMap['demerit'], icon: '👔', isFallback: true };
      config.punctuality = { label: 'Punctuality Mention', ...styleMap['demerit'], icon: '⏰', isFallback: true };
    }

    // Add special event types that aren't consequences
    config.mention = {
      label: 'Mention',
      color: 'text-blue-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      icon: '⚠️'
    };

    config.notification = {
      label: 'Notification',
      color: 'text-cyan-600',
      bgColor: 'bg-gradient-to-br from-cyan-50 to-blue-50',
      borderColor: 'border-cyan-200',
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600',
      icon: '🔔'
    };

    config['contract-consequence'] = {
      label: 'Contract Alternative',
      color: 'text-teal-600',
      bgColor: 'bg-gradient-to-br from-teal-50 to-green-50',
      borderColor: 'border-teal-200',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      icon: '📄'
    };

    config['duplicate-resolution'] = {
      label: 'Duplicate Resolution',
      color: 'text-gray-600',
      bgColor: 'bg-gradient-to-br from-gray-50 to-slate-50',
      borderColor: 'border-gray-200',
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      icon: '📋'
    };

    config['suspension-pending-review'] = {
      label: 'Suspension (Pending Review)',
      color: 'text-rose-600',
      bgColor: 'bg-gradient-to-br from-rose-50 to-pink-50',
      borderColor: 'border-rose-200',
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-600',
      icon: '🚫'
    };

    // Legacy types
    config.consequence = {
      label: 'Consequence',
      color: 'text-indigo-600',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-purple-50',
      borderColor: 'border-indigo-200',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      icon: '⚠️'
    };

    config['mention-rule-consequence'] = {
      label: 'Rule Consequence',
      color: 'text-violet-600',
      bgColor: 'bg-gradient-to-br from-violet-50 to-indigo-50',
      borderColor: 'border-violet-200',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      icon: '⚖️'
    };

    // Unknown type fallback
    config.unknown = {
      label: 'Unknown Type',
      color: 'text-red-700',
      bgColor: 'bg-gradient-to-br from-red-100 to-pink-100',
      borderColor: 'border-red-400',
      iconBg: 'bg-red-200',
      iconColor: 'text-red-700',
      icon: '❓'
    };

    return config;
  }

  /**
   * Get primary filters for DisciplineQueue
   * Automatically includes all consequence types from Firestore
   * @returns {Promise<Array>} Array of filter options
   */
  async getPrimaryFilters(schoolId) {
    const filters = [{ value: null, label: 'All' }];

    // Add mention first
    filters.push({ value: 'mention', label: 'Mentions' });

    // Add all consequence types from Firestore
    const types = await this.getConsequenceTypes(schoolId);
    types.forEach(type => {
      filters.push({
        value: type.value,
        label: (type.plural || type.label || type.value || 'Unknown').charAt(0).toUpperCase() + (type.plural || type.label || type.value || 'unknown').slice(1)
      });
    });

    // Add notification last
    filters.push({ value: 'notification', label: 'Notifications' });

    return filters;
  }
}

// Export singleton instance
export const disciplineConfig = new DisciplineConfigService();

// Default export for convenience
export default disciplineConfig;
